import kebabCase from 'lodash/kebabCase'
import { useI18n } from 'vue-i18n'

// Adapt legacy `tc(key, count)` calls to the vue-i18n v9 `t(key, { n: count })` signature.
function tcArgs(args: unknown[]) {
  if (args && Array.isArray(args) && args.length > 0 && typeof args[0] === 'number') {
    args[0] = { n: args[0] }
  }
  return args
}

export function setupTranslationPrefixer(componentName: string, contextName: string | null | undefined = 'default') {
  const i18n = useI18n()
  /*
   * vue-i18n's `t`, `te` and `d` are overloaded a dozen ways and none of the overloads accepts a
   * spread, so the variadic pass-through below can never be matched against them. Call through a
   * widened signature instead; the arguments come straight from the caller and vue-i18n validates
   * them at runtime anyway.
   */
  const t = i18n.t as (_key: string, ..._args: unknown[]) => string
  const te = i18n.te as (_key: string, ..._args: unknown[]) => boolean
  const d = i18n.d as (_value: Date, _pattern?: string) => string
  const componentName_ = kebabCase(componentName)
  const contextName_ = contextName ? kebabCase(contextName) : undefined
  const prefix_ = contextName_ ? `${componentName_}.${contextName_}` : componentName_

  return {
    translationComponent_: componentName_,
    translationContext_: contextName_,
    translationPrefix_: prefix_,

    // Helper functions that prefix and normalize the translation key
    k_: (key: string) => key && `${prefix_}.${kebabCase(key)}`,
    t_: (key: string, ...args: unknown[]) => key && t(`${prefix_}.${kebabCase(key)}`, ...args),
    tc_: (key: string, ...args: unknown[]) => key && t(`${prefix_}.${kebabCase(key)}`, ...tcArgs(args)),
    te_: (key: string, ...args: unknown[]) => key && te(`${prefix_}.${kebabCase(key)}`, ...args),

    // Helper function that automatically converts strings into Date objects and supports the 'iso' format
    d_: (value: string | Date | undefined, pattern?: string) => {
      if (!value) {
        return ''
      }
      const date = typeof value === 'object' ? value : new Date(value)
      return pattern === 'iso' ? date.toISOString() : d(date, pattern)
    },
  }
}
